import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { generateMqttClient } from '@pages/_loader/initMqttClient.loader';
import type { MqttClient } from 'mqtt';

interface BMSContextState {
  mqttClient: MqttClient | undefined;
  isConnecting: boolean;
  connectionError: Error | null;
}

const initialState: BMSContextState = {
  mqttClient: undefined,
  isConnecting: true,
  connectionError: null,
};

const BMSContext = createContext<BMSContextState>(initialState);

interface BMSContextProviderProps {
  children: ReactNode;
}

export const BMSContextProvider = ({ children }: BMSContextProviderProps) => {
  const [mqttClient, setMqttClient] = useState<MqttClient>();
  const [isConnecting, setIsConnecting] = useState(true);
  const [connectionError, setConnectionError] = useState<Error | null>(null);

  const setupMqttClient = useCallback(async () => {
    try {
      const clientInstance = await generateMqttClient();

      setMqttClient(clientInstance);
      setIsConnecting(clientInstance.reconnecting);
      setConnectionError(null);

      clientInstance.on('connect', () => {
        setIsConnecting(false);
        setConnectionError(null);
      });

      clientInstance.on('error', (err) => {
        setConnectionError(new Error(String(err)));
      });

      clientInstance.on('reconnect', () => {
        setIsConnecting(true);
      });

      return clientInstance;
    } catch (error) {
      setMqttClient(undefined);
      setIsConnecting(false);
      setConnectionError(
        error instanceof Error ? error : new Error(String(error)),
      );
      return undefined;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let clientInstance: MqttClient | null = null;

    setupMqttClient()
      .then((client) => {
        if (isMounted && client) {
          clientInstance = client;
        }
      })
      .catch((error) => {
        if (isMounted) {
          setIsConnecting(false);
          setConnectionError(new Error(`Connection failed: ${error.message}`));
        }
      });

    return () => {
      isMounted = false;
      if (clientInstance) {
        clientInstance?.removeAllListeners();
        clientInstance?.end(true);
      }
    };
  }, [setupMqttClient]);

  const contextValue = useMemo(
    () => ({
      mqttClient,
      isConnecting,
      connectionError,
    }),
    [mqttClient, isConnecting, connectionError],
  );

  return (
    <BMSContext.Provider value={contextValue}>{children}</BMSContext.Provider>
  );
};

export const useBMSContext = (): BMSContextState => {
  const context = useContext(BMSContext);

  if (context === undefined) {
    throw new Error('useBMSContext must be used within a BMSContextProvider');
  }

  return context;
};
